import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, CheckCircle, X, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { API_BASE, API } from '../../lib/api';


export default function ForgotPasswordModal({ onClose }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="dark:bg-[#141414] bg-white dark:border-white/[0.1] border-gray-200 max-w-md rounded-2xl"
      >
        {!sent ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-[#3DD56D]/10 border border-[#3DD56D]/20 flex items-center justify-center">
                  <Mail size={18} className="text-[#3DD56D]" />
                </div>
                <div>
                  <DialogTitle className="text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Reset Password
                  </DialogTitle>
                  <DialogDescription className="text-muted-foreground text-xs">
                    We'll send a reset link to your email
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label htmlFor="reset-email" className="text-sm text-foreground font-medium">
                  Email Address
                </Label>
                <Input
                  id="reset-email"
                  data-testid="forgot-password-email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.1] border-gray-200 dark:text-white text-gray-900 placeholder:text-muted-foreground focus-visible:ring-[#3DD56D] h-11 rounded-xl"
                />
              </div>
              <div className="flex gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-11 rounded-xl dark:border-white/[0.1] dark:hover:bg-white/[0.05]"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="forgot-password-submit"
                  disabled={loading}
                  className="flex-1 h-11 rounded-xl bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold disabled:opacity-50"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Send Link <ArrowRight size={14} />
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex flex-col items-center text-center py-4">
                <div className="w-16 h-16 rounded-full bg-[#3DD56D]/10 border border-[#3DD56D]/25 flex items-center justify-center mb-4">
                  <CheckCircle size={28} className="text-[#3DD56D]" />
                </div>
                <DialogTitle className="text-foreground text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Check Your Email
                </DialogTitle>
                <DialogDescription className="text-muted-foreground text-sm mt-2 leading-relaxed">
                  If <span className="text-foreground font-medium">{email}</span> is registered,
                  you'll receive a password reset link within 2 minutes.
                </DialogDescription>
              </div>
            </DialogHeader>
            <Button
              onClick={onClose}
              className="w-full h-11 rounded-xl bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold mt-2"
            >
              Done
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
